import { Injectable, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';

@Injectable({
  providedIn: 'root'
})
export class PrintService {
  private windowRef: Window | null = null;

  // Inject DOCUMENT for server-side rendering safety and to get the window reference
  constructor(@Inject(DOCUMENT) private document: Document) { 
    this.windowRef = this.document.defaultView;
  }

  /**
   * Generates and prints the content in a new temporary window.
   * @param htmlContent The raw HTML string to be printed (e.g., a receipt).
   * @param styleCss An optional CSS string to include for styling the print-out.
   */
  public printHtmlDocument(htmlContent: string, styleCss?: string): void {
    // 1. Create a hidden iframe element
    const iframe = this.document.createElement('iframe');
    iframe.setAttribute('style', 'visibility: hidden; position: fixed; right: 0; bottom: 0; height: 0; width: 0;');
    
    // 2. Append it to the document body
    this.document.body.appendChild(iframe);

    // Get the iframe's document object
    const iframeDoc = iframe.contentWindow?.document;

    if (!iframeDoc) {
      console.error('Could not access iframe document for printing.');
      this.document.body.removeChild(iframe);
      return;
    }

    // 3. Build the full HTML document content for the iframe
    const documentHtml = `
      <html>
        <head>
          <title>Print Document</title>
          <style>
            /* Essential styles for thermal printing */
            @media print {
              @page { margin: 0; size: auto; } /* Eliminate margins */
              body { 
                margin: 0; 
                max-width: 80mm; /* Example width for thermal printer */
                font-family: monospace; 
                font-size: 12px;
              }
            }
            ${styleCss || ''} /* Inject any custom styles passed in */
          </style>
        </head>
        <body>
          ${htmlContent}
        </body>
      </html>
    `;

    // 4. Write content to the iframe document
    iframeDoc.open();
    iframeDoc.write(documentHtml);
    iframeDoc.close();

    // Wait for content (especially images) to load, then print and clean up
    iframe.onload = () => {
      iframe.contentWindow?.focus(); // Ensure the iframe is focused
      iframe.contentWindow?.print(); // Open the print dialog

      // Clean up the iframe after a short delay
      setTimeout(() => {
        this.document.body.removeChild(iframe);
      }, 500);
    };

    // Fallback for browsers that don't trigger iframe.onload (less common)
    if (!iframe.contentWindow?.print) {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
      setTimeout(() => {
        this.document.body.removeChild(iframe);
      }, 500); 
    }
  }
}
